/**
 * NumericInput - Financial amount input with shorthand parsing
 *
 * Accepts shorthand like 1K, 2.5M, 3B (case-insensitive) and converts
 * to formatted numbers on blur/tab/enter. Stores numeric value internally.
 *
 * Matches workstation styling (22px height, 0.72rem font, same border as <F>).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { inputSx, readonlySx, labelSx, WS } from './WorkstationLayout';

/* ─── Shorthand Parsing ───────────────────────────────────────────────────── */

export function parseShorthand(input: string): { value: number | null; error: string | null } {
  const trimmed = input.trim().toUpperCase();
  if (!trimmed) return { value: null, error: null };

  // Check for invalid letter patterns (two+ consecutive letters that aren't a known suffix)
  if (/[A-Z]{2,}/.test(trimmed.replace(/^-?\d+\.?\d*/, '').replace(/^(MM|BN)$/, ''))) {
    const remainder = trimmed.replace(/^-?\d+\.?\d*\s*/, '');
    if (remainder !== 'MM' && remainder !== 'BN') {
      return { value: null, error: 'Invalid shorthand. Use K, M, or B (e.g., 1K, 2.5M)' };
    }
  }

  const match = trimmed.match(/^(-?\d+\.?\d*)\s*(K|M|B|MM|BN)?$/);
  if (!match) return { value: null, error: 'Invalid number format' };

  const num = parseFloat(match[1]);
  if (isNaN(num)) return { value: null, error: 'Invalid number format' };

  const suffix = match[2];

  const multipliers: Record<string, number> = {
    'K': 1_000,
    'M': 1_000_000,
    'MM': 1_000_000,
    'B': 1_000_000_000,
    'BN': 1_000_000_000,
  };

  const multiplier = suffix ? multipliers[suffix] || 1 : 1;
  return { value: num * multiplier, error: null };
}

/* ─── Number Formatting ───────────────────────────────────────────────────── */

function formatNumber(value: number, precision: number = 2): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

/* ─── Props ────────────────────────────────────────────────────────────────── */

export interface NumericInputProps {
  label: string;
  value: number | string;
  onChange: (numericValue: number) => void;
  // Configuration
  allowShorthand?: boolean;     // default true
  allowNegative?: boolean;      // default false
  allowDecimals?: boolean;      // default true
  displayPrecision?: number;    // default 2
  minValue?: number;            // optional min
  maxValue?: number;            // optional max
  wholeNumberOnly?: boolean;    // default false (for shares/contracts)
  lotSize?: number;             // optional lot size enforcement
  placeholder?: string;
  labelWidth?: number;
  readOnly?: boolean;
  error?: string;
  required?: boolean;
  yellow?: boolean;
  // Styling
  width?: string | number;
}

/* ─── Component ───────────────────────────────────────────────────────────── */

export function NumericInput({
  label,
  value,
  onChange,
  allowShorthand = true,
  allowNegative = false,
  allowDecimals = true,
  displayPrecision = 2,
  minValue,
  maxValue,
  wholeNumberOnly = false,
  lotSize,
  placeholder,
  labelWidth,
  readOnly = false,
  error: externalError,
  required,
  yellow,
  width,
}: NumericInputProps) {
  const theme = useTheme();
  const lw = labelWidth || 90;
  const precision = wholeNumberOnly ? 0 : displayPrecision;

  // Track whether the input is focused
  const [focused, setFocused] = useState(false);
  // Raw text the user is typing
  const [rawText, setRawText] = useState('');
  // Validation error from parsing/constraints
  const [internalError, setInternalError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Derive the current numeric value from props
  const numericValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;

  // When value prop changes externally and we're not focused, sync display
  useEffect(() => {
    if (!focused) {
      if (numericValue === 0 && (value === '' || value === undefined || value === null)) {
        setRawText('');
      } else {
        setRawText(String(numericValue));
      }
    }
  }, [value, focused, numericValue]);

  // Validate and commit the value
  const commitValue = useCallback((text: string) => {
    setInternalError(null);

    if (!text.trim()) {
      onChange(0);
      return;
    }

    let parsed: { value: number | null; error: string | null };

    if (allowShorthand) {
      parsed = parseShorthand(text);
    } else {
      const num = parseFloat(text);
      parsed = isNaN(num)
        ? { value: null, error: 'Invalid number format' }
        : { value: num, error: null };
    }

    if (parsed.error) {
      setInternalError(parsed.error);
      return;
    }

    if (parsed.value === null) {
      onChange(0);
      return;
    }

    let finalValue = parsed.value;

    // Negative check
    if (!allowNegative && finalValue < 0) {
      setInternalError('Negative values not allowed');
      return;
    }

    // Whole number check
    if (wholeNumberOnly && !Number.isInteger(finalValue)) {
      finalValue = Math.round(finalValue);
    }

    // Decimal check
    if (!allowDecimals && !Number.isInteger(finalValue)) {
      finalValue = Math.round(finalValue);
    }

    // Min/max bounds
    if (minValue !== undefined && finalValue < minValue) {
      setInternalError(`Minimum value is ${formatNumber(minValue, precision)}`);
      return;
    }
    if (maxValue !== undefined && finalValue > maxValue) {
      setInternalError(`Maximum value is ${formatNumber(maxValue, precision)}`);
      return;
    }

    // Lot size enforcement
    if (lotSize && lotSize > 0 && finalValue % lotSize !== 0) {
      const nearest = Math.round(finalValue / lotSize) * lotSize;
      finalValue = nearest;
      setInternalError(null); // auto-correct, no error
    }

    onChange(finalValue);
  }, [allowShorthand, allowNegative, allowDecimals, wholeNumberOnly, minValue, maxValue, lotSize, precision, onChange]);

  const handleFocus = () => {
    setFocused(true);
    setInternalError(null);
    // Show raw numeric value for editing (not formatted with commas)
    if (numericValue !== 0) {
      setRawText(String(numericValue));
    } else {
      setRawText('');
    }
  };

  const handleBlur = () => {
    setFocused(false);
    commitValue(rawText);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitValue(rawText);
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRawText(e.target.value);
    setInternalError(null);
  };

  // Display value: formatted when not focused, raw text when focused
  const displayValue = focused
    ? rawText
    : (numericValue !== 0 ? formatNumber(numericValue, precision) : '');

  const error = externalError || internalError;

  const bgColor = yellow ? alpha(theme.palette.warning.main, 0.15) : readOnly ? WS.BG_READONLY : WS.BG_INPUT;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: width || '100%' }}>
      <label style={{ ...labelSx, minWidth: lw, maxWidth: lw, textAlign: 'right' }}>
        {label}{required && <span style={{ color: theme.palette.error.main }}>*</span>}
      </label>
      <div style={{ flex: 1 }}>
        <input
          ref={inputRef}
          style={{
            ...inputSx,
            borderColor: error ? theme.palette.error.main : theme.palette.divider,
            backgroundColor: bgColor,
            textAlign: 'right',
          }}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          readOnly={readOnly}
          placeholder={placeholder || (allowShorthand ? 'e.g. 2.5M' : '')}
        />
        {error && (
          <div style={{ fontSize: '0.6rem', color: theme.palette.error.main, marginTop: 1 }}>{error}</div>
        )}
      </div>
    </div>
  );
}

export default NumericInput;
